import { useState, useEffect } from 'react';
import './excel-download-modal.css';
import { reportsAPI } from '../../../../config/reports-api';
import { Download, X, Check } from 'lucide-react';

const ExcelDownloadModal = ({ isOpen, onClose, cardData, reportRange, customStartDate, customEndDate }) => {
  const [isGenerating, setIsGenerating] = useState(true);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    if (isOpen && cardData) {
      setIsGenerating(true);
      setError(null);
      generateExcel();
    }
  }, [isOpen, cardData, reportRange, customStartDate, customEndDate]);

  const getUserTypeForAPI = (cardKey) => {
    const userTypeMap = {
      'totalUsersCreated': 'total_users',
      'totalOrders': 'total_orders',
      'deliveredOrders': 'delivered_orders',
      'cancelledOrders': 'cancelled_orders',
      'orderedUsers': 'ordered_users',
      'usersWithMultipleOrders': 'repeat_customers',
      'zeroOrderUsers': 'zero_order_users',
      'usersCreatedButNotOrdered': 'users_created_but_not_ordered',
      'newlyJoinedUsers': 'newly_joined_users',
      'newlyJoinedAndOrdered': 'newly_joined_and_ordered',
      'newlyJoinedAndHavingCart': 'newly_joined_and_having_cart'
    };

    return userTypeMap[cardKey] || cardKey;
  };

  const generateExcel = async () => {
    try {
      if (!cardData?.key) {
        setError('No card data selected');
        setIsGenerating(false);
        return;
      }

      const userType = getUserTypeForAPI(cardData.key);

      const result = await reportsAPI.createExcelReport(
        userType,
        reportRange,
        reportRange === 'custom' ? customStartDate : null,
        reportRange === 'custom' ? customEndDate : null
      );

      if (result.success) {
        setFileName(result.fileName);
        setDownloadUrl(result.downloadUrl);
        setIsGenerating(false);
      } else {
        setError(result.error);
        setIsGenerating(false);
      }
    } catch (err) {
      setError('Failed to generate Excel file: ' + err.message);
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="excel-modal-overlay">
      <div className="excel-modal-content">
        {isGenerating ? (
          <>
            <div className="excel-loading-spinner-large"></div>
            <h2 className="excel-modal-title">
              Generating {cardData?.label} Excel file...
            </h2>
            <p className="excel-modal-description">
              Please wait while we process your request...
            </p>
          </>
        ) : error ? (
          <div className="excel-container">
            <div className="excel-icon-container excel-error-icon">
              <X size={32} />
            </div>
            <h2 className="excel-modal-title excel-error-title">
              Error Generating File
            </h2>
            <p className="excel-modal-description">{error}</p>
            <button onClick={onClose} className="excel-modal-close-btn" aria-label="Close">
              <X size={18} />
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="excel-icon-container excel-success-icon">
              <Check size={32} strokeWidth={3} />
            </div>
            <h2 className="excel-modal-title excel-success-title">
              Excel File Generated Successfully!
            </h2>
            <p className="excel-modal-description">
              <strong>File:</strong> {fileName}
            </p>
            <div className="excel-modal-buttons">
              <button onClick={handleDownload} className="excel-modal-btn excel-download-btn">
                <Download size={18} />
                Download Excel
              </button>
              <button onClick={onClose} className="excel-modal-close-btn" aria-label="Close">
                <X size={18} />
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExcelDownloadModal;